import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Database, 
  Table2, 
  Search, 
  ChevronRight,
  ChevronDown,
  FileJson,
  RefreshCw,
  Copy,
  Check,
  Filter,
  BarChart3,
  Trash2,
  ShieldAlert,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import useAuthStore from "@/store/authStore";
import { useHasAnyRole } from "@/utils/roleGuard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DBExplorer() {
  const { user, userRoles } = useAuthStore();
  // Page-level access check — same role-allowlist used by the route
  // guard (App.js::RoleRoute) and the sidebar (Layout.js::canSee).
  // Three sources of truth all converge on `utils/roleGuard.hasAnyRole`
  // so there's no longer a way for one gate to drift out of sync with
  // the others. (That drift is what allowed Jenny — TECHADMIN_VENDOR
  // with `users.role='branch_user'` — to be silently blocked.)
  const isAdmin = useHasAnyRole(['MASTER_ADMIN', 'master_admin', 'TECHADMIN_VENDOR']);
  // Surface the user's role(s) on the Access-Denied screen — kept for
  // backward compat with the original `user?.role` reference below.
  const _legacyRoleForDisplay = user?.role || (userRoles || []).join(', ') || 'unknown';
  
  // State
  const [databases, setDatabases] = useState([]);
  const [selectedDb, setSelectedDb] = useState("");
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("");
  const [documents, setDocuments] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  // Search state
  const [searchField, setSearchField] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchExact, setSearchExact] = useState(false);
  
  // Compare state
  const [compareField, setCompareField] = useState("rm_id");
  const [compareValue, setCompareValue] = useState("");
  const [compareResults, setCompareResults] = useState(null);
  
  // Aggregate state
  const [groupByField, setGroupByField] = useState("");
  const [aggregateResults, setAggregateResults] = useState(null);
  const [error, setError] = useState(null);

  // Database Ops state (single-collection purge — operates on the
  // collection picked in the left pane; allowlist governs which collections
  // are actually purgeable, master data stays excluded by policy).
  const [opsAllowlist, setOpsAllowlist] = useState([]);
  const [opsSample, setOpsSample] = useState([]);
  const [opsCount, setOpsCount] = useState(0);
  const [opsBusy, setOpsBusy] = useState(false);
  const [opsConfirmOpen, setOpsConfirmOpen] = useState(false);
  const [opsMode, setOpsMode] = useState("backup_then_delete"); // or "delete_only"

  // Derived: is the currently-selected (left-pane) collection eligible for purge?
  const opsAllowed = !!selectedCollection && opsAllowlist.some((c) => c.name === selectedCollection);

  // Check access — see top of component; computed via useHasAnyRole.

  useEffect(() => {
    // Always try to fetch on mount for admins, plus debug info
    console.log("DBExplorer - User:", user);
    console.log("DBExplorer - isAdmin:", isAdmin);
    if (isAdmin) {
      fetchDatabases();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (selectedDb) {
      fetchCollections(selectedDb);
      setSelectedCollection("");
      setDocuments([]);
    }
  }, [selectedDb]);

  useEffect(() => {
    if (selectedDb && selectedCollection) {
      fetchSampleDocuments();
    }
  }, [selectedDb, selectedCollection]);

  const fetchDatabases = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/db-explorer/databases`);
      console.log("DBExplorer - API Response:", res.data);
      setDatabases(res.data.databases || []);
      // Jun 2026: clear any stale error message from an earlier in-flight
      // call (e.g. the no-token call that races zustand hydration before
      // the auth interceptor has the bearer token to attach).
      setError(null);
      if (res.data.databases?.length > 0 && !selectedDb) {
        setSelectedDb(res.data.databases[0].name);
      }
    } catch (err) {
      console.error("DBExplorer - API Error:", err);
      setError(err.response?.data?.detail || err.message || "Failed to fetch databases");
      toast.error("Failed to fetch databases: " + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
  };

  const fetchCollections = async (dbName) => {
    try {
      const res = await axios.get(`${API}/admin/db-explorer/databases/${dbName}/collections`);
      // Sort alphabetically by collection name (case-insensitive) per user spec.
      const list = (res.data.collections || []).slice().sort((a, b) =>
        (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
      );
      setCollections(list);
    } catch (error) {
      toast.error("Failed to fetch collections");
    }
  };

  const fetchSampleDocuments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/db-explorer/databases/${selectedDb}/collections/${selectedCollection}/sample?limit=20`
      );
      setDocuments(res.data.documents);
      setTotalDocs(res.data.total);
    } catch (error) {
      toast.error("Failed to fetch documents");
    }
    setLoading(false);
  };

  const handleSearch = async () => {
    if (!searchField || !searchValue) {
      toast.error("Enter field and value to search");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/db-explorer/databases/${selectedDb}/collections/${selectedCollection}/search`,
        { params: { field: searchField, value: searchValue, exact: searchExact, limit: 50 } }
      );
      setDocuments(res.data.documents);
      setTotalDocs(res.data.total);
      toast.success(`Found ${res.data.total} documents`);
    } catch (error) {
      toast.error("Search failed");
    }
    setLoading(false);
  };

  const handleCompare = async () => {
    if (!compareField || !compareValue) {
      toast.error("Enter field and value to compare");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/db-explorer/compare/${selectedCollection || 'raw_materials'}`,
        { params: { field: compareField, value: compareValue } }
      );
      setCompareResults(res.data);
      toast.success("Comparison complete");
    } catch (error) {
      toast.error("Compare failed");
    }
    setLoading(false);
  };

  const handleAggregate = async () => {
    if (!groupByField) {
      toast.error("Enter a field to group by");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/db-explorer/databases/${selectedDb}/collections/${selectedCollection}/aggregate`,
        { params: { group_by: groupByField, limit: 30 } }
      );
      setAggregateResults(res.data);
      toast.success("Aggregation complete");
    } catch (error) {
      toast.error("Aggregation failed");
    }
    setLoading(false);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(JSON.stringify(text, null, 2));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Copied to clipboard");
  };

  // ─── Wipeout Data handlers ────────────────────────────────────────────────
  const fetchOpsAllowlist = async (dbName) => {
    try {
      const params = dbName ? `?db_name=${encodeURIComponent(dbName)}` : '';
      const res = await axios.get(`${API}/admin/db-explorer/database-ops/allowed-collections${params}`);
      // Backend returns either ["name", …] (no db) or [{name, count}, …] (with db).
      const raw = res.data.collections || [];
      const normalised = raw.map((c) => (typeof c === 'string' ? { name: c, count: null } : c));
      setOpsAllowlist(normalised);
    } catch (e) {
      setOpsAllowlist([]);
    }
  };

  const fetchOpsSample = async (coll) => {
    if (!coll || !selectedDb) return;
    setOpsSample([]); setOpsCount(0);
    try {
      const res = await axios.get(
        `${API}/admin/db-explorer/databases/${selectedDb}/collections/${coll}/sample?limit=5`
      );
      setOpsSample(res.data.documents || []);
      setOpsCount(res.data.total || 0);
    } catch (e) {
      toast.error("Failed to load sample documents");
    }
  };

  const handleDatabaseOps = async () => {
    if (!selectedCollection || !selectedDb) return;
    setOpsBusy(true);
    try {
      // 1) Auto-download backup if requested. If the backup fails, abort.
      if (opsMode === "backup_then_delete") {
        try {
          const res = await axios.get(
            `${API}/admin/db-explorer/databases/${selectedDb}/collections/${selectedCollection}/backup`,
            { responseType: 'blob' }
          );
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          link.setAttribute('download', `${selectedDb}_${selectedCollection}_backup_${ts}.json`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.info("Backup downloaded — keep it safe.");
        } catch (e) {
          toast.error("Backup download failed — aborting delete to protect your data.");
          setOpsBusy(false);
          setOpsConfirmOpen(false);
          return;
        }
      }

      // 2) Purge
      const res = await axios.post(
        `${API}/admin/db-explorer/databases/${selectedDb}/collections/${selectedCollection}/purge`,
        { confirm: "PROCEED" }
      );
      toast.success(`Deleted ${res.data.deleted_count} rows from ${selectedCollection}.`);
      setOpsConfirmOpen(false);
      // Refresh sample + sidebar count
      fetchOpsSample(selectedCollection);
      fetchCollections(selectedDb);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wipeout Data failed");
    }
    setOpsBusy(false);
  };

  useEffect(() => {
    if (isAdmin) fetchOpsAllowlist(selectedDb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, selectedDb]);

  // Whenever the user picks a different collection in the left pane, refresh
  // the Wipeout sample / count for that collection (only if it's allowlisted).
  useEffect(() => {
    if (selectedCollection && selectedDb && opsAllowlist.some((c) => c.name === selectedCollection)) {
      fetchOpsSample(selectedCollection);
    } else {
      setOpsSample([]);
      setOpsCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCollection, selectedDb, opsAllowlist]);

  // Clear the wipeout sample/count when the user switches databases (the
  // collection picker resets in the existing selectedDb effect).
  useEffect(() => {
    setOpsSample([]);
    setOpsCount(0);
  }, [selectedDb]);
  // ──────────────────────────────────────────────────────────────────────────

  const formatValue = (value) => {
    if (value === null || value === undefined) return <span className="text-gray-400">null</span>;
    if (typeof value === 'boolean') return <span className="text-purple-600">{value.toString()}</span>;
    if (typeof value === 'number') return <span className="text-blue-600">{value}</span>;
    if (typeof value === 'string' && value.length > 100) return value.substring(0, 100) + '...';
    if (Array.isArray(value)) return <span className="text-orange-600">[{value.length} items]</span>;
    if (typeof value === 'object') return <span className="text-green-600">{'{...}'}</span>;
    return String(value);
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <Database className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700">Access Denied</h2>
        <p className="text-gray-500">Database Explorer is only available to administrators.</p>
        <p className="text-xs text-gray-400 mt-2">Role: {user?.role || 'none'}</p>
        <Button className="mt-4" onClick={fetchDatabases}>Try Loading Anyway</Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6" />
            Database Explorer
          </h1>
          <p className="text-muted-foreground">Browse and query MongoDB databases</p>
        </div>
        <Button variant="outline" onClick={fetchDatabases} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-700 font-medium">Error loading databases:</p>
            <p className="text-red-600 text-sm">{error}</p>
            <Button className="mt-2" size="sm" onClick={fetchDatabases}>Retry</Button>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && databases.length === 0 && (
        <Card className="bg-zinc-50">
          <CardContent className="p-8 text-center text-muted-foreground">
            <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
            <p>Loading databases...</p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !error && databases.length === 0 && (
        <Card className="bg-zinc-50">
          <CardContent className="p-8 text-center text-muted-foreground">
            <Database className="w-8 h-8 mx-auto mb-2" />
            <p>No databases found</p>
            <p className="text-xs mt-2">This could mean the MongoDB connection is using a different URL in production.</p>
            <Button className="mt-4" onClick={fetchDatabases}>Try Again</Button>
          </CardContent>
        </Card>
      )}

      {/* Database Cards */}
      {databases.length > 0 && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {databases.map((db) => (
          <Card 
            key={db.name}
            className={`cursor-pointer transition-all ${selectedDb === db.name ? 'ring-2 ring-orange-500 bg-orange-50' : 'hover:bg-gray-50'}`}
            onClick={() => setSelectedDb(db.name)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database className={`w-5 h-5 ${selectedDb === db.name ? 'text-orange-500' : 'text-gray-500'}`} />
                  <span className="font-mono font-medium">{db.name}</span>
                </div>
                {selectedDb === db.name && <Check className="w-4 h-4 text-orange-500" />}
              </div>
              <div className="mt-2 flex gap-4 text-sm text-muted-foreground">
                <span>{db.collections_count} collections</span>
                <span>{db.total_documents.toLocaleString()} docs</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {/* Main Content */}
      {selectedDb && (
        <div className="grid grid-cols-12 gap-6">
          {/* Collections Sidebar */}
          <div className="col-span-3">
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Table2 className="w-4 h-4" />
                  Collections ({collections.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-[500px] overflow-y-auto">
                {collections.map((coll) => (
                  <div
                    key={coll.name}
                    className={`px-3 py-2 cursor-pointer border-l-2 transition-all ${
                      selectedCollection === coll.name 
                        ? 'bg-orange-50 border-orange-500' 
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedCollection(coll.name)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm truncate">{coll.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {coll.document_count.toLocaleString()}
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Document Viewer */}
          <div className="col-span-9">
            <Tabs defaultValue="browse">
              <TabsList>
                  <TabsTrigger value="browse">Browse</TabsTrigger>
                  <TabsTrigger value="search">Search</TabsTrigger>
                  <TabsTrigger value="compare">Compare DBs</TabsTrigger>
                  <TabsTrigger value="aggregate">Aggregate</TabsTrigger>
                  <TabsTrigger value="database-ops" data-testid="tab-database-ops" className="text-red-700">
                    <ShieldAlert className="w-3.5 h-3.5 mr-1" />Wipeout Data
                  </TabsTrigger>
                </TabsList>

                {/* Browse Tab */}
                <TabsContent value="browse" className="mt-4">
                  {!selectedCollection ? (
                    <Card className="bg-zinc-50">
                      <CardContent className="p-8 text-center text-muted-foreground">
                        <Table2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Select a collection from the sidebar to browse documents</p>
                      </CardContent>
                    </Card>
                  ) : (
                  <Card>
                    <CardHeader className="py-3 flex-row items-center justify-between">
                      <CardTitle className="text-sm font-medium">
                        {selectedCollection} <span className="text-muted-foreground">({totalDocs.toLocaleString()} total)</span>
                      </CardTitle>
                      <Button size="sm" variant="outline" onClick={fetchSampleDocuments}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Reload
                      </Button>
                    </CardHeader>
                    <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                      {documents.map((doc, idx) => (
                        <div key={idx} className="border-b last:border-b-0">
                          <div 
                            className="px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                            onClick={() => setExpandedDoc(expandedDoc === idx ? null : idx)}
                          >
                            <div className="flex items-center gap-2">
                              {expandedDoc === idx ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              <span className="font-mono text-sm">
                                {doc.id?.substring(0, 8) || doc.rm_id || doc.name || `Document ${idx + 1}`}
                              </span>
                              {doc.rm_id && <Badge variant="outline" className="text-xs">{doc.rm_id}</Badge>}
                              {doc.category && <Badge className="text-xs">{doc.category}</Badge>}
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); copyToClipboard(doc, idx); }}
                            >
                              {copiedId === idx ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            </Button>
                          </div>
                          {expandedDoc === idx && (
                            <div className="px-4 py-2 bg-zinc-50 border-t">
                              <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                                {JSON.stringify(doc, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                  )}
                </TabsContent>

                {/* Search Tab */}
                <TabsContent value="search" className="mt-4 space-y-4">
                  {!selectedCollection ? (
                    <Card className="bg-zinc-50">
                      <CardContent className="p-8 text-center text-muted-foreground">
                        <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Select a collection from the sidebar to search</p>
                      </CardContent>
                    </Card>
                  ) : (
                  <>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex gap-4 items-end">
                        <div className="flex-1">
                          <Label>Field</Label>
                          <Input 
                            placeholder="e.g., rm_id, name, category"
                            value={searchField}
                            onChange={(e) => setSearchField(e.target.value)}
                          />
                        </div>
                        <div className="flex-1">
                          <Label>Value</Label>
                          <Input 
                            placeholder="Search value..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            id="exact" 
                            checked={searchExact}
                            onChange={(e) => setSearchExact(e.target.checked)}
                          />
                          <Label htmlFor="exact" className="text-sm">Exact</Label>
                        </div>
                        <Button onClick={handleSearch} disabled={loading}>
                          <Search className="w-4 h-4 mr-2" />
                          Search
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {documents.length > 0 && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Results ({totalDocs})</CardTitle>
                      </CardHeader>
                      <CardContent className="p-0 max-h-[400px] overflow-y-auto">
                        {documents.map((doc, idx) => (
                          <div key={idx} className="px-4 py-2 border-b last:border-b-0 hover:bg-gray-50">
                            <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                              {JSON.stringify(doc, null, 2)}
                            </pre>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                  </>
                  )}
                </TabsContent>

                {/* Compare Tab */}
                <TabsContent value="compare" className="mt-4 space-y-4">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground mb-4">
                        Compare a document across all databases to see differences
                      </p>
                      <div className="flex gap-4 items-end">
                        <div className="w-48">
                          <Label>Field</Label>
                          <Select value={compareField} onValueChange={setCompareField}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="rm_id">rm_id</SelectItem>
                              <SelectItem value="id">id</SelectItem>
                              <SelectItem value="name">name</SelectItem>
                              <SelectItem value="sku_id">sku_id</SelectItem>
                              <SelectItem value="code">code</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1">
                          <Label>Value</Label>
                          <Input 
                            placeholder="e.g., ACC_269"
                            value={compareValue}
                            onChange={(e) => setCompareValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCompare()}
                          />
                        </div>
                        <Button onClick={handleCompare} disabled={loading}>
                          <FileJson className="w-4 h-4 mr-2" />
                          Compare
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {compareResults && (
                    <div className="grid grid-cols-1 gap-4">
                      {compareResults.results.map((result, idx) => (
                        <Card key={idx} className={result.found ? 'border-green-200' : 'border-red-200'}>
                          <CardHeader className="py-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Database className="w-4 h-4" />
                              {result.database}
                              <Badge variant={result.found ? 'default' : 'destructive'}>
                                {result.found ? 'Found' : 'Not Found'}
                              </Badge>
                            </CardTitle>
                          </CardHeader>
                          {result.found && (
                            <CardContent className="py-2">
                              <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap bg-zinc-50 p-2 rounded">
                                {JSON.stringify(result.document, null, 2)}
                              </pre>
                            </CardContent>
                          )}
                          {!result.found && result.reason && (
                            <CardContent className="py-2 text-sm text-muted-foreground">
                              {result.reason}
                            </CardContent>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Aggregate Tab */}
                <TabsContent value="aggregate" className="mt-4 space-y-4">
                  {!selectedCollection ? (
                    <Card className="bg-zinc-50">
                      <CardContent className="p-8 text-center text-muted-foreground">
                        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Select a collection from the sidebar to aggregate</p>
                      </CardContent>
                    </Card>
                  ) : (
                  <>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex gap-4 items-end">
                        <div className="flex-1">
                          <Label>Group By Field</Label>
                          <Input 
                            placeholder="e.g., category, status, source_type"
                            value={groupByField}
                            onChange={(e) => setGroupByField(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAggregate()}
                          />
                        </div>
                        <Button onClick={handleAggregate} disabled={loading}>
                          <BarChart3 className="w-4 h-4 mr-2" />
                          Aggregate
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {aggregateResults && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">
                          Grouped by: {aggregateResults.group_by}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <table className="w-full text-sm">
                          <thead className="bg-zinc-100">
                            <tr>
                              <th className="text-left px-4 py-2">Value</th>
                              <th className="text-right px-4 py-2">Count</th>
                              <th className="px-4 py-2 w-1/2">Distribution</th>
                            </tr>
                          </thead>
                          <tbody>
                            {aggregateResults.results.map((r, idx) => {
                              const maxCount = Math.max(...aggregateResults.results.map(x => x.count));
                              const pct = (r.count / maxCount) * 100;
                              return (
                                <tr key={idx} className="border-t">
                                  <td className="px-4 py-2 font-mono">{r.value || '(null)'}</td>
                                  <td className="px-4 py-2 text-right">{r.count.toLocaleString()}</td>
                                  <td className="px-4 py-2">
                                    <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                                      <div 
                                        className="bg-orange-500 h-full rounded-full"
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>
                  )}
                  </>
                  )}
                </TabsContent>

                {/* Wipeout Data Tab — single-collection purge, allowlisted.
                    Operates on whichever collection is currently selected in
                    the LEFT PANE (no separate dropdown). */}
                <TabsContent value="database-ops" className="mt-4 space-y-4">
                  <Card className="border-red-200">
                    <CardHeader className="py-3 bg-red-50 border-b border-red-200">
                      <CardTitle className="text-sm flex items-center gap-2 text-red-800">
                        <ShieldAlert className="w-4 h-4" />
                        Wipeout Data — destructive · master_admin only
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-4">
                      <p className="text-xs text-muted-foreground">
                        Deletes <strong>every row</strong> of the collection currently selected in the left pane.
                        Master data (RMs, vendors, buyers, models, brands, BOMs, users…) is intentionally excluded —
                        use each module's own row-level UI to remove those.
                        <br />
                        <span className="text-amber-700 font-medium">
                          Audit: every purge writes an entry to <code>audit_logs</code>.
                        </span>
                      </p>

                      {!selectedCollection ? (
                        <div className="rounded border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center text-xs text-muted-foreground" data-testid="db-ops-pick-collection-hint">
                          Pick a collection in the left pane to target it for wipeout.
                        </div>
                      ) : !opsAllowed ? (
                        <div className="rounded border border-amber-300 bg-amber-50 p-4 text-xs text-amber-900" data-testid="db-ops-not-allowed-hint">
                          <div className="font-semibold mb-1">
                            <code>{selectedCollection}</code> is not eligible for Wipeout Data.
                          </div>
                          Only transactional collections can be purged here. Master-data collections
                          (raw_materials, vendors, buyers, models, brands, BOMs, users, roles, etc.)
                          must be edited row-by-row from their own module UIs.
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-muted-foreground">Target collection:</span>
                            <code className="font-mono font-semibold" data-testid="db-ops-target-collection">{selectedCollection}</code>
                            <span className="font-mono font-bold text-red-700" data-testid="db-ops-target-count">
                              {opsCount.toLocaleString()} row{opsCount === 1 ? '' : 's'}
                            </span>
                          </div>

                          {opsSample.length > 0 && (
                            <Card className="bg-zinc-50">
                              <CardHeader className="py-2">
                                <CardTitle className="text-xs">Sample (first 5 documents)</CardTitle>
                              </CardHeader>
                              <CardContent className="p-2">
                                <pre className="text-[11px] font-mono overflow-x-auto whitespace-pre-wrap max-h-72">
                                  {JSON.stringify(opsSample, null, 2)}
                                </pre>
                              </CardContent>
                            </Card>
                          )}
                          {opsSample.length === 0 && (
                            <p className="text-xs text-muted-foreground italic">Collection is empty.</p>
                          )}

                          {opsCount > 0 && (
                            <div className="flex gap-2 pt-2 border-t">
                              <Button
                                variant="outline"
                                className="border-amber-400 text-amber-800 hover:bg-amber-50"
                                disabled={opsBusy}
                                onClick={() => { setOpsMode("backup_then_delete"); setOpsConfirmOpen(true); }}
                                data-testid="db-ops-delete-with-backup-btn"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Delete with Backup
                              </Button>
                              <Button
                                variant="destructive"
                                disabled={opsBusy}
                                onClick={() => { setOpsMode("delete_only"); setOpsConfirmOpen(true); }}
                                data-testid="db-ops-delete-btn"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Dialog open={opsConfirmOpen} onOpenChange={setOpsConfirmOpen}>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-red-700 flex items-center gap-2">
                          <ShieldAlert className="w-5 h-5" />
                          {opsMode === "backup_then_delete" ? "Confirm delete (with backup)" : "Confirm DELETE — no backup"}
                        </DialogTitle>
                        <DialogDescription className="pt-2 space-y-2">
                          <span className="block">
                            You're about to permanently delete <span className="font-mono font-bold text-red-700">{opsCount.toLocaleString()}</span> rows from <code>{selectedDb}.{selectedCollection}</code>.
                          </span>
                          {opsMode === "backup_then_delete" ? (
                            <span className="block text-amber-700">
                              A JSON backup of the full collection will auto-download first. If the backup fails, the delete is aborted.
                            </span>
                          ) : (
                            <span className="block text-red-700 font-medium">
                              ⚠️ No backup will be taken. This action is irreversible — there is no built-in rollback. Use "Delete with Backup" instead unless you're absolutely certain.
                            </span>
                          )}
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setOpsConfirmOpen(false)} disabled={opsBusy}>Cancel</Button>
                        <Button
                          variant="destructive"
                          onClick={handleDatabaseOps}
                          disabled={opsBusy}
                          data-testid="db-ops-proceed-btn"
                        >
                          {opsBusy ? "Working..." : "Proceed"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TabsContent>
              </Tabs>
          </div>
        </div>
      )}
    </div>
  );
}
